import { forwardRef, type SyntheticEvent } from "react";

import type { ZoomState } from "../../lib/imageViewer";

export interface PanoViewerProps {
  src: string;
  alt?: string;
  /**
   * 位移 / 缩放（受控）。
   *
   * 手势（拖动环视、滚轮、长按放大镜、← → 切换、0 还原）统一由 `ImageViewer` 处理：
   * 两层各挂一套 pointer 监听会互相抢事件，也会让放大镜在拖动时落错位置。
   * 这里只负责「整图按容器高度铺满 + 横向溢出」的全景布局与变换。
   */
  zoom: ZoomState;
  onLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
  onError?: () => void;
}

/**
 * 全景扩展：等距柱状的宽幅母图（宽高比 > 2 或显式 `panorama: true`）。
 *
 * 和普通看图（`object-fit: contain` 装进容器）不同，全景按**高度**铺满、宽度按原图比例溢出，
 * 于是 1 倍时就能横向拖动看到画外（参考 `pano_viewer.js` 的「拖动环视」，但不引 WebGL / three.js）。
 * 缩放与位移都是 CSS transform，放大镜 canvas 从原图取像素，因此任意缩放 / 平移后都对得上。
 */
export const PanoViewer = forwardRef<HTMLImageElement, PanoViewerProps>(function PanoViewer(
  { src, alt = "", zoom, onLoad, onError },
  ref,
) {
  return (
    <div
      className="dp-pano-frame"
      data-pano-zoom={zoom.s}
      style={{ transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.s})` }}
    >
      <img
        ref={ref}
        src={src}
        alt={alt}
        draggable={false}
        onDragStart={(event) => event.preventDefault()}
        onLoad={onLoad}
        onError={onError}
      />
    </div>
  );
});
